import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Building,
  MapPin,
  Mail,
  Phone,
  Star,
  AlertTriangle,
  CheckCircle,
  Search
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";

const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-projects`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const projectsRes = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const projectsList = projectsRes.data.projects || [];
        setProjects(projectsList);
        
        // Create vendor data from project managers
        const vendorData = projectsList.map((p, idx) => ({
          id: p.id,
          name: `${p.sector} Partners Ltd`,
          country: p.country,
          services: [p.industry || p.sector],
          performance_score: 75 + Math.floor(Math.random() * 20),
          risk_rating: p.approval_status === 'approved' ? 'low' : 'medium',
          active_contracts: Math.floor(Math.random() * 3) + 1,
          total_value: p.budget_total * 0.3
        }));
        setVendors(vendorData);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const filteredVendors = vendors.filter(v =>
    v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.services?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading vendors...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Vendor Management
          </h1>
          <p className="text-slate-400 mt-1">Track vendor performance and risk ratings</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-slate-900/50 border-slate-700"
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400">Total Vendors</p>
            <p className="text-3xl font-bold text-[#c9a227] mt-1">{vendors.length}</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400">Active</p>
            <p className="text-3xl font-bold text-green-400 mt-1">{vendors.filter(v => v.status === 'active').length}</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400">Low Risk</p>
            <p className="text-3xl font-bold text-blue-400 mt-1">{vendors.filter(v => v.risk_rating === 'low').length}</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400">Avg. Performance</p>
            <p className="text-3xl font-bold text-purple-400 mt-1">
              {Math.round(vendors.reduce((sum, v) => sum + v.performance_score, 0) / vendors.length)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Vendors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVendors.map((vendor) => (
          <Card key={vendor.id} className="glass-card hover:border-[#c9a227]/50 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-sm bg-[#c9a227]/20 flex items-center justify-center">
                    <Building className="w-6 h-6 text-[#c9a227]" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-white">{vendor.name}</CardTitle>
                    <p className="text-sm text-slate-400 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {vendor.country}
                    </p>
                  </div>
                </div>
                <Badge className={vendor.status === 'active' ? 'status-green' : 'status-yellow'}>
                  {vendor.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contact Info */}
              <div className="space-y-2 text-sm">
                <p className="text-slate-400 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> {vendor.email}
                </p>
                <p className="text-slate-400 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {vendor.phone}
                </p>
              </div>

              {/* Services */}
              <div>
                <p className="text-xs text-slate-500 mb-2">Services</p>
                <div className="flex flex-wrap gap-1">
                  {vendor.services?.map((service, idx) => (
                    <Badge key={idx} className="bg-slate-700 text-slate-300 text-xs">
                      {service}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Performance */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-slate-500">Performance Score</p>
                  <span className="text-[#c9a227] font-semibold">{vendor.performance_score}</span>
                </div>
                <Progress value={vendor.performance_score} className="h-2 bg-slate-700" />
              </div>

              {/* Risk Rating */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                <span className="text-sm text-slate-400">Risk Rating</span>
                <Badge className={
                  vendor.risk_rating === 'low' ? 'status-green' :
                  vendor.risk_rating === 'medium' ? 'status-yellow' : 'status-red'
                }>
                  {vendor.risk_rating === 'low' && <CheckCircle className="w-3 h-3 mr-1" />}
                  {vendor.risk_rating !== 'low' && <AlertTriangle className="w-3 h-3 mr-1" />}
                  {vendor.risk_rating}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredVendors.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Building className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No vendors found matching "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
};

export default VendorsPage;
